import { faUser } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { NavLink } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'
import { UserLevelBar } from '~/features/progression/components/UserLevelBar'
import { useUserPayoutWallet } from '~/features/wallet/hooks/useUserPayoutWallet'
import { useUserTokens } from '~/hooks/useUserTokens'
import { useUser } from '../api/useUser'

/**
 * The viewer's avatar: their first operative if they hold one, the generic mark otherwise.
 *
 * Same source as the dashboard identity panel, deliberately — one person should not have two faces
 * on the same site. Both read `useUserTokens`, and React Query dedupes them by key, so the pair
 * costs one request per five minutes rather than one each.
 *
 * The name stays `EmptyAvatar` because three call sites and other modules import it; only what it
 * renders has changed.
 */
export const EmptyAvatar = () => {
    const { wallet, hasWalletConnected, isFetched } = useUserPayoutWallet()
    const { data: tokens } = useUserTokens()
    // Portrait crop where it exists; the full-body art is the fallback for responses cached
    // before `pfp_url` was served.
    const avatar = tokens?.[0]?.pfp_url ?? tokens?.[0]?.image_url

    return (
        // No `overflow-hidden` on this element: the payout warning is positioned at `left-full
        // top-full`, i.e. deliberately outside the circle, so clipping here would swallow it. The
        // rounding that the artwork needs goes on the inner element instead.
        <div className='relative h-8 w-8'>
            <div
                className={twMerge(
                    'flex h-full w-full flex-col items-center justify-center overflow-hidden rounded-full',
                    // The pink disc is the placeholder's own styling, not a frame around artwork.
                    avatar ? 'bg-black ring-1 ring-white/20' : 'bg-[#e47ba6]'
                )}
            >
                {avatar ? (
                    <img src={avatar} alt='' className='h-full w-full object-cover' />
                ) : (
                    <FontAwesomeIcon icon={faUser} color='#b04572' className='w-5 h-5' />
                )}
            </div>
            {isFetched && hasWalletConnected && wallet === undefined && (
                <div className='absolute left-full top-full z-10 -translate-x-1/2 -translate-y-1/2 bg-yellow-500/80 flex flex-row items-center justify-center px-2 py-0 text-xs rounded font-bold border'>
                    1
                </div>
            )}
        </div>
    )
}

export const UserMenuItem = () => {
    const { data, isFetched } = useUser()
    return (
        isFetched && (
            <NavLink to='/account' className='group grid grid-cols-[repeat(3,max-content)] items-center gap-x-2'>
                <EmptyAvatar />
                <div className='text-sm uppercase font-bold group-hover:underline underline-offset-2'>
                    {data.username}
                </div>
            </NavLink>
        )
    )
}

/**
 * The avatar and name without the link wrapper, for use as a dropdown trigger — an anchor nested
 * inside the trigger button is invalid markup and would navigate instead of opening the menu.
 */
export const UserMenuLabel = () => {
    const { data, isFetched } = useUser()
    return (
        isFetched && (
            <span className='grid grid-cols-[repeat(2,max-content)] items-center gap-x-2'>
                <EmptyAvatar />
                {/*
                  The bar is ~144px and the menu bar is already carrying four groups plus three
                  money figures by the time it reaches here. Between md and lg there is no room for
                  it, so that range keeps the compact avatar trigger and the bar returns at lg.
                */}
                <span className='hidden lg:block'>
                    <UserLevelBar username={data.username} />
                </span>
            </span>
        )
    )
}

export const UserMenuItemMobile = () => {
    const { data, isFetched } = useUser()
    return (
        isFetched && (
            <NavLink to='/account' className='grid grid-cols-[max-content,1fr] items-center gap-x-2 bg-black rounded-3xl p-2'>
                <EmptyAvatar />
                {/* Rounded to sit inside the pill the mobile menu wraps every row in. */}
                <UserLevelBar username={data.username} className='w-full justify-center rounded-3xl' />
            </NavLink>
        )
    )
}
